import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { api } from '@/lib/api';
import { useDialogFocusRestore } from '@/lib/hooks/useDialogFocusRestore';
import type { AppStatsResponse } from '@/types/app';
import type { Stack } from '@/types/stack';
import { StatsCharts } from './StatsCharts';

type Range = '1h' | '6h' | '24h' | '7d';

interface StackStatsDialogProps {
  stack: Stack | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function StatsBody({ stack, range }: { stack: Stack; range: Range }) {
  // The result remembers which stack/range it belongs to, so "loading" is derived (no setState in the effect body).
  const [result, setResult] = useState<{ id: number; range: Range; data: AppStatsResponse | null } | null>(null);
  const loading = !result || result.id !== stack.id || result.range !== range;
  const stats = loading ? null : result.data;

  useEffect(() => {
    let cancelled = false;
    api.stacks
      .stats(stack.id, { range })
      .then((data) => { if (!cancelled) setResult({ id: stack.id, range, data }); })
      .catch((err) => {
        console.error(err);
        if (!cancelled) setResult({ id: stack.id, range, data: null });
      });
    return () => { cancelled = true; };
  }, [stack.id, range]);

  const formatBytes = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  };

  return (
    <>
      {loading ? (
          <div className="py-12 flex justify-center items-center gap-2 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading stats...
          </div>
        ) : stats ? (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="border rounded-md p-3">
                <p className="text-xs text-muted-foreground">Containers</p>
                <p className="text-xl font-bold">{stats.containers.running} / {stats.containers.total}</p>
              </div>
              <div className="border rounded-md p-3">
                <p className="text-xs text-muted-foreground">Total CPU</p>
                <p className="text-xl font-bold">{stats.current?.cpu_percent?.toFixed(2) || '0'}%</p>
              </div>
              <div className="border rounded-md p-3">
                <p className="text-xs text-muted-foreground">Memory Used</p>
                <p className="text-xl font-bold">{stats.current ? formatBytes(stats.current.memory_used) : '0 B'}</p>
              </div>
              <div className="border rounded-md p-3">
                <p className="text-xs text-muted-foreground">Network I/O</p>
                <p className="text-xl font-bold text-sm truncate">
                  {stats.current ? `${formatBytes(stats.current.network_in_bytes)} / ${formatBytes(stats.current.network_out_bytes)}` : '0 B'}
                </p>
              </div>
            </div>

            <StatsCharts history={stats.history || []} networkMode="counter" />
          </div>
        ) : (
          <div className="py-8 text-center text-muted-foreground">
            No metrics available for this stack.
          </div>
        )}
    </>
  );
}

export function StackStatsDialog({ stack, open, onOpenChange }: StackStatsDialogProps) {
  const [range, setRange] = useState<Range>('1h');
  const { captureOpener, restoreFocus } = useDialogFocusRestore();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="max-w-4xl max-h-[90vh] overflow-y-auto" onOpenAutoFocus={captureOpener} onCloseAutoFocus={restoreFocus}>
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>Stack Metrics: {stack?.name}</DialogTitle>
            <select
              aria-label="Time range"
              value={range}
              onChange={(e) => setRange(e.target.value as Range)}
              className="mr-4 rounded-md border border-input bg-background px-3 py-1 text-sm"
            >
              <option value="1h">Last Hour</option>
              <option value="6h">Last 6 Hours</option>
              <option value="24h">Last 24 Hours</option>
              <option value="7d">Last 7 Days</option>
            </select>
          </div>
        </DialogHeader>
        {open && stack ? <StatsBody stack={stack} range={range} /> : null}
      </DialogContent>
    </Dialog>
  );
}
